<template>
  <nav class="fpn" :aria-label="navLabel">
    <div class="fpn-progress">
      <div class="fpn-progress-line">
        <span class="fpn-progress-label">{{ progressLabel }}</span>
        <span class="fpn-progress-pct">{{ progressPct }} %</span>
      </div>
      <div class="fpn-progress-track">
        <div class="fpn-progress-fill" :style="{ width: `${progressPct}%` }"></div>
      </div>
    </div>

    <!-- SOUS 769 px, la liste devient un SÉLECTEUR. Repliée en bande
         horizontale, elle ferait défiler des entrées hors champ ; en colonne,
         elle mangerait l'écran avant le premier champ. -->
    <div class="fpn-picker">
      <label class="fpn-picker-label" :for="`${testidPrefix}-select`">{{ pickerLabel }}</label>
      <select
        :id="`${testidPrefix}-select`"
        class="form-control"
        :value="active"
        :data-testid="`${testidPrefix}-select`"
        @change="$emit('update:active', $event.target.value)"
      >
        <option v-for="(section, i) in sections" :key="section.key" :value="section.key">
          {{ i + 1 }}. {{ section.label }} — {{ section.stateText }}
        </option>
      </select>
    </div>

    <!-- `scroll-shadow` : quand le plan est plus long que la place, une ombre
         courte le dit. La barre de défilement de macOS est en superposition —
         elle ne prend aucune largeur et ne se montre qu'en défilant — donc une
         liste coupée par le bord du cadre se lit comme une liste complète. -->
    <ul class="fpn-list scroll-shadow">
      <li v-for="section in sections" :key="section.key">
        <button
          type="button"
          class="fpn-item"
          :class="{ 'is-active': section.key === active }"
          :aria-current="section.key === active ? 'true' : undefined"
          :data-testid="`${testidPrefix}-${section.key}`"
          @click="$emit('update:active', section.key)"
        >
          <span
            class="fpn-dot"
            :class="{ 'is-done': section.done, 'is-readonly': section.readOnly }"
            aria-hidden="true"
          >
            <i v-if="section.done" class="fas fa-check"></i>
          </span>
          <span class="fpn-label">{{ section.label }}</span>
          <span v-if="section.missing" class="fpn-missing">{{ section.missing }}</span>
          <span v-else-if="section.count" class="fpn-count">{{ section.count }}</span>
          <span v-else-if="section.readOnly" class="fpn-readonly">lecture</span>
          <span class="sr-only">{{ section.stateText }}</span>
        </button>
      </li>
    </ul>

    <!-- Ce qu'une page a d'utile à garder sous les yeux pendant toute la
         saisie — l'audience d'une mission, un rappel de dossier. Le plan est
         le seul endroit qui ne défile pas. -->
    <div v-if="$slots.footer" class="fpn-foot">
      <slot name="footer" />
    </div>
  </nav>
</template>

<script setup>
/**
 * LE PLAN D'UN FORMULAIRE LONG — une entrée par section, l'état de chacune,
 * et le compte de ce qui manque.
 *
 * Extrait de `quote-besoin/BesoinPlanNav.vue` (tunnel « Analyse du besoin »)
 * quand un deuxième écran en a eu besoin : la publication d'une mission depuis
 * l'espace formateur. Rien n'y était propre à l'analyse du besoin sauf trois
 * libellés, devenus des props.
 *
 * EN COLONNE ET NON EN ONGLETS : les titres de section montent à cinquante
 * caractères ; une douzaine de ces libellés côte à côte débordent, et un
 * onglet hors champ n'existe plus.
 *
 * L'ENTRÉE COURANTE se marque par le fond du panneau et le poids de son texte.
 * Pas de filet d'accent au bord : le contraste suffit, et il ne fabrique pas un
 * ornement de plus.
 *
 * PRÉSENTATIONNEL : des props entrent, `update:active` sort.
 */
import { computed } from 'vue'

const props = defineProps({
  /**
   * Les sections du plan :
   * `{ key, label, done, readOnly, missing, count, stateText }`.
   * `missing` = nombre de réponses obligatoires qui manquent (0 = rien) ;
   * `count` = repère informatif affiché à sa place quand rien ne manque.
   */
  sections: {
    type: Array,
    default: () => []
  },
  /** Clé de la section affichée. */
  active: {
    type: String,
    default: null
  },
  /** Sections faites / sections à faire (alimente la barre de progression). */
  done: {
    type: Number,
    default: 0
  },
  total: {
    type: Number,
    default: 0
  },
  /** Phrase de progression, écrite par la page (« 3 sections remplies sur 5 »). */
  progressLabel: {
    type: String,
    default: ''
  },
  /** Libellé d'accessibilité de la navigation. */
  navLabel: {
    type: String,
    default: 'Plan du formulaire'
  },
  /** Libellé du sélecteur mobile. */
  pickerLabel: {
    type: String,
    default: 'Section'
  },
  /** Préfixe des `data-testid` (`<prefix>-<clé de section>`, `<prefix>-select`). */
  testidPrefix: {
    type: String,
    default: 'form-plan'
  }
})

defineEmits(['update:active'])

const progressPct = computed(() => (props.total ? Math.round((props.done / props.total) * 100) : 0))
</script>

<style scoped>
.fpn {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--color-bg-secondary);
}

/* Le compte : la réponse à « où j'en suis » sans descendre nulle part. */
.fpn-progress {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  padding: var(--spacing-4) var(--spacing-5) var(--spacing-3);
}

.fpn-progress-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-2);
}

.fpn-progress-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.fpn-progress-pct {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.fpn-progress-track {
  height: 6px;
  border-radius: var(--radius-full, 999px);
  background: var(--color-border);
  overflow: hidden;
}

.fpn-progress-fill {
  height: 100%;
  border-radius: var(--radius-full, 999px);
  background: var(--color-primary);
  transition: width var(--transition-base);
}

.fpn-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0;
  padding: var(--spacing-1) var(--spacing-3) var(--spacing-4);
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Le plan est posé sur le fond secondaire : sans cette teinte, les deux
     couches de masquage de `scroll-shadow` peindraient du blanc par-dessus. */
  --scroll-shadow-bg: var(--color-bg-secondary);
}

.fpn-item {
  /* POSITIONNÉ POUR TENIR SON TEXTE D'ÉCRAN — sans cela le `.sr-only` de
     l'entrée, en `position: absolute`, prend le corps du document pour bloc
     conteneur : il ÉCHAPPE au découpage de la bande défilante et étire la page
     en largeur. La bande, elle, défile correctement. */
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  width: 100%;
  min-height: 44px;
  padding: var(--spacing-2) var(--spacing-3);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.fpn-item:hover {
  background: var(--color-bg-hover);
}

.fpn-item:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* L'entrée courante prend le fond du panneau de droite : c'est le contraste
   qui dit « ici », pas un filet ajouté au bord. */
.fpn-item.is-active {
  background: var(--color-bg-primary);
}

.fpn-dot {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-full, 999px);
  border: 1.5px solid var(--color-border-dark);
  background: var(--color-bg-primary);
  color: var(--color-text-inverse);
  font-size: 0.625rem;
  line-height: 1;
}

.fpn-dot.is-done {
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
}

/* Les blocs qu'on ne remplit pas — un texte qui se lit, un total qui se
   calcule : leur pastille ne doit pas se lire comme « pas encore fait ». */
.fpn-dot.is-readonly {
  border-style: dashed;
}

.fpn-label {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-sm);
  line-height: 1.35;
  font-weight: var(--font-weight-normal);
  color: var(--color-text-secondary);
}

.fpn-item.is-active .fpn-label {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

/* Ce qui manque se compte ici aussi : le pied dit le total, le plan dit où. */
.fpn-missing {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 var(--spacing-2);
  border-radius: var(--radius-full, 999px);
  background: var(--color-warning-lightest);
  color: #8a5a00;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
}

/* Le repère informatif d'une section qui ne bloque rien (« 2 dates », « 69 ») :
   il occupe la place du compteur de manques, jamais les deux à la fois. */
.fpn-count {
  flex: 0 0 auto;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.fpn-readonly {
  flex: 0 0 auto;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

/* Un filet sépare le pied de la liste : quand le plan défile, sa dernière
   entrée visible s'arrête juste au-dessus, et sans ce trait le contenu du pied
   se lit comme une entrée de plus. */
.fpn-foot {
  flex: 0 0 auto;
  padding: var(--spacing-3) var(--spacing-3) var(--spacing-4);
  border-top: 1px solid var(--color-border);
}

.fpn-picker {
  display: none;
}

/* ENTRE 769 ET 1024 px, la colonne n'a plus la place : le plan passe au-dessus
   du panneau en bande défilante, comme le fait `ui/Tabs.vue` quand la largeur
   manque. Sa hauteur est BORNÉE — sans cela la piste `auto` de la grille prend
   la hauteur naturelle des entrées et ne laisse presque rien au corps. */
@media (max-width: 1024px) {
  .fpn-progress {
    padding: var(--spacing-3) var(--spacing-md) var(--spacing-2);
  }

  .fpn-list {
    flex: 0 0 auto;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 var(--spacing-md) var(--spacing-3);
    gap: var(--spacing-1);
    /* LES OMBRES TOURNENT AVEC LA BANDE. Posées en haut et en bas, elles ne
       diraient rien d'un débordement devenu horizontal — et c'est justement là
       qu'une entrée sort du champ sans laisser de trace, la barre de
       défilement de macOS étant en superposition. */
    background-image:
      linear-gradient(to right, var(--scroll-shadow-bg, var(--color-bg-secondary)) 30%, transparent),
      linear-gradient(to right, transparent, var(--scroll-shadow-bg, var(--color-bg-secondary)) 70%),
      radial-gradient(farthest-side at 0 50%, rgba(15, 23, 42, 0.18), transparent),
      radial-gradient(farthest-side at 100% 50%, rgba(15, 23, 42, 0.18), transparent);
    background-position: 0 0, 100% 0, 0 0, 100% 0;
    background-size: 24px 100%, 24px 100%, 12px 100%, 12px 100%;
  }

  .fpn-item {
    width: auto;
    flex: 0 0 auto;
  }

  .fpn-label {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .fpn-foot {
    padding: 0 var(--spacing-md) var(--spacing-3);
  }
}

@media (max-width: 768px) {
  .fpn-picker {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
    padding: 0 var(--spacing-md) var(--spacing-3);
  }

  .fpn-list {
    display: none;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
